import { cloneElement } from "react";
import { FORM_FIELDS } from "./constants";
import {
  BOTULINUM_FORM,
  BOTULINUM_FORM_ARCHIVED,
  MIGRAINE_INJECTION_FORM,
} from "./forms/botulinum";
import { DIAGNOSES_FORM } from "./forms/diagnoses";
import { SCALENE_BLOCK_FORM } from "./forms/scalene-block";
import { UPPER_AND_LOWER_FORM } from "./forms/upper-and-lower-injection";
import { ProceduresResults, Field, Form } from "./types";
import React from "react";

class DocumentBuilder<T extends Record<string, Field>> {
  private workingLine = (<></>);
  private builtLine = (<></>);
  private fieldsAhead = false;
  private nextDelimeter = "";
  private onOutWrappers: JSX.Element[] = [];
  private workingWrappers: JSX.Element[] = [];
  private readonly values: T;

  constructor(values: T) {
    this.values = values;
  }

  rawAppend(item: string | JSX.Element): DocumentBuilder<T> {
    this.workingLine = (
      <>
        {this.spreadChildren(this.workingLine)}
        {item}
      </>
    );
    return this;
  }

  wrapOnOut(node: JSX.Element): DocumentBuilder<T> {
    this.onOutWrappers.push(node);
    return this;
  }

  checkFieldCondition(field: string): boolean {
    return (
      this.values[field] && this.values[field].value.toLowerCase() === "true"
    );
  }

  conditionallyWrapLine(condition: string, node: JSX.Element) {
    if (!this.checkFieldCondition(condition)) return this;
    return this.wrapLine(node);
  }

  wrapLine(node: JSX.Element): DocumentBuilder<T> {
    this.workingWrappers.push(node);
    return this;
  }

  newline(): DocumentBuilder<T> {
    this.fieldsAhead = false;
    this.workingLine = (
      <>
        {this.spreadChildren(this.workingLine)}
        <br />
      </>
    );
    return this;
  }

  addText(text: string): DocumentBuilder<T> {
    this.workingLine = (
      <>
        {this.isEmptyFragment(this.workingLine)
          ? ""
          : this.spreadChildren(this.workingLine)}
        {`${this.fieldsAhead ? this.nextDelimeter || " / " : ""}${text}`}
      </>
    );
    this.fieldsAhead = true;
    this.setNextDelimeter("");
    return this;
  }

  conditionallyAppend(condition: string, text: string) {
    if (!this.checkFieldCondition(condition)) {
      this.setNextDelimeter("");
      return this;
    }
    this.addText(text);
    this.setNextDelimeter(" ");
    return this;
  }

  conditionallyAddField(
    condition: string,
    id: string,
    nextDelimeter: string = "",
  ) {
    if (!this.checkFieldCondition(condition)) {
      this.setNextDelimeter("");
      return this;
    }
    return this.addField(id, nextDelimeter);
  }

  addField(id: string, nextDelimeter: string = ""): DocumentBuilder<T> {
    if (!this.values[id]) {
      this.setNextDelimeter("");
      return this;
    }
    this.addText(
      this.values[id].isArray
        ? this.values[id].value.join(", ")
        : this.values[id].value,
    );
    this.setNextDelimeter(nextDelimeter);
    return this;
  }

  setNextDelimeter(delimeter: string): DocumentBuilder<T> {
    this.nextDelimeter = delimeter;
    return this;
  }

  forceNextDelimeter(nextDelimeter: string): DocumentBuilder<T> {
    if (this.nextDelimeter) this.addText("");
    this.setNextDelimeter(nextDelimeter);
    return this;
  }

  private isEmptyFragment = (element: JSX.Element): boolean => {
    return (
      element.type === React.Fragment &&
      React.Children.count(element?.props?.children) === 0
    );
  };

  private spreadChildren = (element: JSX.Element): JSX.Element[] => {
    return element?.props?.children?.map((child: JSX.Element) => child);
  };

  buildLine(): DocumentBuilder<T> {
    let content = <>{this.workingLine}</>;
    if (!this.isEmptyFragment(this.workingLine))
      this.workingWrappers
        .reverse()
        .forEach((node) => (content = cloneElement(node, {}, content)));
    this.builtLine = (
      <>
        {this.spreadChildren(this.builtLine)}
        {content}
      </>
    );

    this.workingLine = <></>;
    this.fieldsAhead = false;
    this.workingWrappers = [];
    return this;
  }

  out(): JSX.Element {
    this.buildLine();
    let result = cloneElement(this.builtLine);
    [...this.onOutWrappers].reverse().forEach((node) => {
      result = cloneElement(node, {}, result);
    });

    return result;
  }
}

export const buildDiagnosesSection = (diagnoses: Form): JSX.Element => {
  const fields = FORM_FIELDS[DIAGNOSES_FORM].fields;
  const diagnosesBuilder = new DocumentBuilder(diagnoses.fields)
    .wrapLine(<li></li>)
    .addField(fields.DX_OPT_1)
    .buildLine()
    .conditionallyWrapLine(fields.TBI_DX_CHECKBOX, <li></li>)
    .conditionallyAddField(fields.TBI_DX_CHECKBOX, fields.TBI_DETAILED_DX, ": ")
    .conditionallyAddField(fields.TBI_DX_CHECKBOX, fields.TBI_FURTHER_DX)
    .buildLine()
    .wrapLine(<li></li>)
    .addField(fields.CT_SPINAL_DX_1, ": ")
    .addField(fields.CT_SPINAL_DX_1_SPEX)
    .buildLine()
    .wrapLine(<li></li>)
    .addField(fields.CT_SPINAL_DX_2, ": ")
    .addField(fields.CT_SPINAL_DX_2_SPEX)
    .buildLine()
    .conditionallyWrapLine(fields.CT_SPINAL_DX_3_CHECKBOX, <li></li>)
    .conditionallyAddField(
      fields.CT_SPINAL_DX_3_CHECKBOX,
      fields.CT_SPINAL_DX_3,
      ": ",
    )
    .conditionallyAddField(
      fields.CT_SPINAL_DX_3_CHECKBOX,
      fields.CT_SPINAL_DX_3_SPEX,
    )
    .buildLine()
    .wrapLine(<li></li>)
    .addField(fields.LS_SPINAL_DX_1, ": ")
    .addField(fields.LS_SPINAL_DX_1_SPEX)
    .buildLine()
    .wrapLine(<li></li>)
    .addField(fields.LS_SPINAL_DX_2, ": ")
    .addField(fields.LS_SPINAL_DX_2_SPEX)
    .buildLine()
    .conditionallyWrapLine(fields.LS_SPINAL_DX_3_CHECKBOX, <li></li>)
    .conditionallyAddField(
      fields.LS_SPINAL_DX_3_CHECKBOX,
      fields.LS_SPINAL_DX_3,
      ": ",
    )
    .conditionallyAddField(
      fields.LS_SPINAL_DX_3_CHECKBOX,
      fields.LS_SPINAL_DX_3_SPEX,
    )
    .buildLine()
    .wrapLine(<li></li>)
    .addField(fields.UE_DX_1, ": ")
    .addField(fields.UE_DX_1_SPEX)
    .buildLine()
    .conditionallyWrapLine(fields.UE_DX_2_CHECKBOX, <li></li>)
    .conditionallyAddField(fields.UE_DX_2_CHECKBOX, fields.UE_DX_2, ": ")
    .conditionallyAddField(fields.UE_DX_2_CHECKBOX, fields.UE_DX_2_SPEX)
    .buildLine()
    .wrapLine(<li></li>)
    .addField(fields.LE_DX_1, ": ")
    .addField(fields.LE_DX_1_SPEX)
    .buildLine()
    .conditionallyWrapLine(fields.LE_DX_2_CHECKBOX, <li></li>)
    .conditionallyAddField(fields.LE_DX_2_CHECKBOX, fields.LE_DX_2, ": ")
    .conditionallyAddField(fields.LE_DX_2_CHECKBOX, fields.LE_DX_2_SPEX)
    .buildLine()
    .wrapLine(<li></li>)
    .addField(fields.FUNCTIONAL_DX_1, ": ")
    .addField(fields.FUNCTIONAL_DX_1_SPEX)
    .buildLine()
    .conditionallyWrapLine(fields.FUNCTIONAL_DX_2_CHECKBOX, <li></li>)
    .conditionallyAddField(
      fields.FUNCTIONAL_DX_2_CHECKBOX,
      fields.FUNCTIONAL_DX_2,
      ": ",
    )
    .conditionallyAddField(
      fields.FUNCTIONAL_DX_2_CHECKBOX,
      fields.FUNCTIONAL_DX_2_SPEX,
    )
    .buildLine()
    .wrapLine(<li></li>)
    .addField(fields.DX_OPT_2)
    .buildLine();

  return diagnosesBuilder.out();
};

export const buildPastProcedures = (data: ProceduresResults) => {
  data.notes.sort((a, b) => b.epoch - a.epoch);
  const html = new DocumentBuilder({});
  data.notes.forEach((note) => {
    return note.forms.forEach((form) => {
      const fields = FORM_FIELDS[form.id]?.fields;

      const container = new DocumentBuilder({});
      container
        .wrapOnOut(<div className="data-table"></div>)
        .wrapOnOut(<div className="row"></div>);
      container
        .rawAppend(
          <span className="label">
            <b>{note.date}:</b>
            <br></br>
          </span>,
        )
        .buildLine();

      switch (form.id) {
        case SCALENE_BLOCK_FORM:
          let scaleneBuilder = new DocumentBuilder(form.fields).wrapOnOut(
            <span className="value"></span>,
          );
          scaleneBuilder
            .wrapLine(<b></b>)
            .rawAppend("Scalene Block - ")
            .addField(fields.SIDES_INJECTED)
            .addField(fields.LOCATIONS)
            .addField(fields.FURTHER_INJECTION_DETAILS)
            .addField(fields.FIRST_INJECTATE, " ")
            .addField(fields.FIRST_INJECTATE_QUANTITY)
            .setNextDelimeter(", ")
            .addField(fields.SECOND_INJECTATE, " ")
            .addField(fields.SECOND_INJECTATE_QUANTITY)
            .buildLine()
            .newline()
            .addField(fields.OUTCOME, ". ")
            .addField(fields.POST_PROCEDURE_COMMENTS)
            .buildLine();

          return html.rawAppend(
            container.rawAppend(scaleneBuilder.out()).out(),
          );

        case BOTULINUM_FORM:
          let botulinumBuilder = new DocumentBuilder(form.fields).wrapOnOut(
            <span className="value"></span>,
          );
          botulinumBuilder
            .wrapLine(<b></b>)
            .rawAppend("Botulinum Toxin - ")
            .addField(fields.INJECTATES, " ")
            .addField(fields.INJECTATE_QUANTITY)
            .addField(fields.FURTHER_INJECTION_DETAILS)
            .addField(fields.SIDE_1, " ")
            .addField(fields.UNITS_1, " ")
            .addField(fields.LOCATION_1)
            .addField(fields.SIDE_2, " ")
            .addField(fields.UNITS_2, " ")
            .addField(fields.LOCATION_2)
            .addField(fields.SIDE_3, " ")
            .addField(fields.UNITS_3, " ")
            .addField(fields.LOCATION_3)
            .addField(fields.SIDE_4, " ")
            .addField(fields.UNITS_4, " ")
            .addField(fields.LOCATION_4)
            .addField(fields.SIDE_5, " ")
            .addField(fields.UNITS_5, " ")
            .addField(fields.LOCATION_5)
            .conditionallyAddField(
              fields.ADDITIONAL_LOCATIONS_CHECKBOX,
              fields.ADDITIONAL_SIDE_1,
              " ",
            )
            .conditionallyAddField(
              fields.ADDITIONAL_LOCATIONS_CHECKBOX,
              fields.ADDITIONAL_UNITS_1,
              " ",
            )
            .conditionallyAddField(
              fields.ADDITIONAL_LOCATIONS_CHECKBOX,
              fields.ADDITIONAL_LOCATION_1,
            )
            .conditionallyAddField(
              fields.ADDITIONAL_LOCATIONS_CHECKBOX,
              fields.ADDITIONAL_SIDE_2,
              " ",
            )
            .conditionallyAddField(
              fields.ADDITIONAL_LOCATIONS_CHECKBOX,
              fields.ADDITIONAL_UNITS_2,
              " ",
            )
            .conditionallyAddField(
              fields.ADDITIONAL_LOCATIONS_CHECKBOX,
              fields.ADDITIONAL_LOCATION_2,
            )
            .conditionallyAddField(
              fields.ADDITIONAL_LOCATIONS_CHECKBOX,
              fields.ADDITIONAL_SIDE_3,
              " ",
            )
            .conditionallyAddField(
              fields.ADDITIONAL_LOCATIONS_CHECKBOX,
              fields.ADDITIONAL_UNITS_3,
              " ",
            )
            .conditionallyAddField(
              fields.ADDITIONAL_LOCATIONS_CHECKBOX,
              fields.ADDITIONAL_LOCATION_3,
            )
            .buildLine()
            .newline()
            .addField(fields.OUTCOME, ". ")
            .addField(fields.POST_PROCEDURE_COMMENTS)
            .buildLine();

          return html.rawAppend(
            container.rawAppend(botulinumBuilder.out()).out(),
          );

        case MIGRAINE_INJECTION_FORM:
          let migraineBuilder = new DocumentBuilder(form.fields).wrapOnOut(
            <span className="value"></span>,
          );
          migraineBuilder
            .wrapLine(<b></b>)
            .rawAppend("Migraine Injection - ")
            .addField(fields.INJECTATE, " - ")
            .addField(fields.TOTAL_UNITS)
            .addField(fields.FURTHER_INJECTION_DETAILS)
            .conditionallyAppend(fields.IS_CORRUGATOR, "Corrugator - ")
            .conditionallyAddField(
              fields.IS_CORRUGATOR,
              fields.CORRUGATOR_TOTAL_UNITS,
              ", ",
            )
            .conditionallyAppend(fields.IS_PROCERUS, "Procerus - ")
            .conditionallyAddField(
              fields.IS_PROCERUS,
              fields.PROCERUS_TOTAL_UNITS,
              ", ",
            )
            .conditionallyAppend(fields.IS_FRONTALIS, "Frontalis - ")
            .conditionallyAddField(
              fields.IS_FRONTALIS,
              fields.FRONTALIS_TOTAL_UNITS,
              ", ",
            )
            .conditionallyAppend(fields.IS_TEMPORALIS, "Temporalis - ")
            .conditionallyAddField(
              fields.IS_TEMPORALIS,
              fields.TEMPORALIS_TOTAL_UNITS,
              ", ",
            )
            .conditionallyAppend(fields.IS_OCCIPITALIS, "Occipitalis - ")
            .conditionallyAddField(
              fields.IS_OCCIPITALIS,
              fields.OCCIPITALIS_TOTAL_UNITS,
              ", ",
            )
            .conditionallyAppend(
              fields.IS_CERVICAL_PARASPINALS,
              "Paraspinals - ",
            )
            .conditionallyAddField(
              fields.IS_CERVICAL_PARASPINALS,
              fields.CERVICAL_PARASPINALS_TOTAL_UNITS,
              ", ",
            )
            .conditionallyAppend(fields.IS_TRAPEZIUS, "Trapezius - ")
            .conditionallyAddField(
              fields.IS_TRAPEZIUS,
              fields.TRAPEZIUS_TOTAL_UNITS,
            )
            .buildLine()
            .newline()
            .addField(fields.OUTCOME, ". ")
            .addField(fields.POST_PROCEDURE_COMMENTS)
            .buildLine();

          return html.rawAppend(
            container.rawAppend(migraineBuilder.out()).out(),
          );

        case BOTULINUM_FORM_ARCHIVED:
          let botulinumBuilder_Archived = new DocumentBuilder(
            form.fields,
          ).wrapOnOut(<span className="value"></span>);
          botulinumBuilder_Archived
            .rawAppend("Botulinum Toxin - ")
            .wrapLine(<b></b>)
            .addField(fields.SIDES_INJECTED)
            .addField(fields.LOCATIONS)
            .addField(fields.FURTHER_INJECTION_DETAILS)
            .addField(fields.INJECTATES, " ")
            .addField(fields.INJECTATE_QUANTITY)
            .buildLine()
            .newline()
            .addField(fields.OUTCOME, ". ")
            .addField(fields.POST_PROCEDURE_COMMENTS)
            .buildLine();

          return html.rawAppend(
            container.rawAppend(botulinumBuilder_Archived.out()).out(),
          );

        case UPPER_AND_LOWER_FORM:
          let upperLowerInjectionBuilder = new DocumentBuilder(
            form.fields,
          ).wrapOnOut(<span className="value"></span>);

          if (form.fields[fields.WAS_HYDRODISSECTION_PROCEDURE_CHECKBOX]) {
            upperLowerInjectionBuilder
              .rawAppend(`Hydrodissection - `)
              .addField(fields.HYDRODISSECTION_INJECTION_SITES)
              .addField(fields.HYDRODISSECTION_SIDE)
              .wrapLine(<b></b>)
              .buildLine()
              .newline();
          }

          if (form.fields[fields.WAS_PRP_PROCEDURE_CHECKBOX]) {
            upperLowerInjectionBuilder
              .rawAppend("PRP - ")
              .addField(fields.PRP_PROCEDURE_AMOUNT_OF_BLOOD_DRAWN)
              .addField(fields.PRP_PROCEDURE_FURTHER_DETAILS)
              .wrapLine(<b></b>)
              .buildLine()
              .newline();
          }

          upperLowerInjectionBuilder
            .wrapLine(<b></b>)
            .addField(fields.SIDES_INJECTED)
            .addField(fields.UPPER_BODY_INJECTION_SITES, ", ")
            .addField(fields.LOWER_BODY_INJECTION_SITES)
            .addField(fields.FURTHER_INJECTION_DETAILS)
            .addField(fields.FIRST_INJECTATE, " ")
            .addField(fields.FIRST_INJECTATE_QUANTITY)
            .setNextDelimeter(", ")
            .addField(fields.SECOND_INJECTATE, " ")
            .addField(fields.SECOND_INJECTATE_QUANTITY)
            .buildLine()
            .newline();

          upperLowerInjectionBuilder
            .addField(fields.OUTCOME, ". ")
            .addField(fields.PERCENT_RELIEF_DETAILS, " relief.")
            .forceNextDelimeter(" ")
            .addField(fields.POST_PROCEDURE_COMMENTS);

          return html.rawAppend(
            container.rawAppend(upperLowerInjectionBuilder.out()).out(),
          );

        default:
          return "";
      }
    });
  });
  return html.out();
};

export const buildUnauthorizedHtmlResponse = () => {
  const clientId = "l5vFllbXnjtYzj46TO8M1It6RtC9HkqG8JC4bYWc";
  return `<html><div>
             <p>You don't seem to be authenticated. Please follow this <a target="_blank" href="https://drchrono.com/o/authorize/?redirect_uri=https%3A%2F%2Fhgg7yccm6rpp5vy4yrr7cbfioi0yvdrd.lambda-url.us-west-2.on.aws%2F&response_type=code&client_id=${clientId}">link</a> to re-authenticate. You may need to refresh this page after authenticating. If the problem continues, there may be an issue with the DrChrono APIs.</p>
    </div></html>`;
};
